import React from 'react';
import { Button } from '../../ui/Button/Button';
import { Field } from '../ui/Field';
import { Sheet } from '../ui/Sheet';
import { DALI_BRIDGE_ADDRESS, DALI_MASTER } from '../../../lib/rcu/catalog/dali';
import { DALI_BRIDGE_ADDRESS_RANGE, nextFreeBridgeAddress, nextFreeBridgeMaster } from '../../../lib/rcu/query/lookup';
import type { DaliBridge } from '../../../lib/rcu/model';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface BridgeSheetProps {
  isOpen: boolean;
  /** null creates a bridge, otherwise the one being edited. */
  bridge: DaliBridge | null;
  onClose: () => void;
}

const MASTER_RANGE: readonly number[] = Array.from(
  { length: DALI_MASTER.MAX - DALI_MASTER.MIN + 1 },
  (_unused, index) => DALI_MASTER.MIN + index,
);

/** A row of address buttons, greyed where the value is already taken. */
const AddressPicker: React.FC<{
  label: string;
  hint: string;
  values: readonly number[];
  taken: ReadonlySet<number>;
  selected: number;
  onSelect: (value: number) => void;
}> = ({ label, hint, values, taken, selected, onSelect }) => (
  <div>
    <span className="mb-1.5 block text-sm font-medium text-[#333333]">{label}</span>
    <div className="grid grid-cols-8 gap-1.5">
      {values.map((candidate) => {
        const isTaken = taken.has(candidate);
        const isSelected = candidate === selected;
        return (
          <button
            key={candidate}
            type="button"
            disabled={isTaken}
            onClick={() => onSelect(candidate)}
            className={`h-9 rounded-lg border text-sm font-medium transition-colors ${
              isSelected
                ? 'border-[#D4A054] bg-[#D4A054] text-white'
                : 'border-[#E0E0E0] bg-white text-[#333333] hover:border-[#D4A054]'
            } disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:text-[#CCCCCC]`}
          >
            {candidate}
          </button>
        );
      })}
    </div>
    <span className="mt-1.5 block text-xs text-[#666666]">{hint}</span>
  </div>
);

/**
 * Add or edit a DALI bridge: a module with a rotary address and its own DALI
 * bus. The bridge carries no guest-facing channels, so this sheet only records
 * where it sits in the cabinet.
 */
export const BridgeSheet: React.FC<BridgeSheetProps> = ({ isOpen, bridge, onClose }) => {
  const config = useRcuConfigStore((state) => state.config);
  const addDaliBridge = useRcuConfigStore((state) => state.addDaliBridge);
  const updateDaliBridge = useRcuConfigStore((state) => state.updateDaliBridge);
  const report = useCommandFeedback();

  const [address, setAddress] = React.useState(
    bridge?.address ?? nextFreeBridgeAddress(config) ?? DALI_BRIDGE_ADDRESS.MIN,
  );
  const [master, setMaster] = React.useState(
    bridge?.master ?? nextFreeBridgeMaster(config) ?? DALI_MASTER.MIN,
  );
  const [label, setLabel] = React.useState(bridge?.label ?? '');

  const others = config.daliBridges.filter((current) => current.id !== bridge?.id);
  const takenAddresses = new Set(others.map((current) => current.address));
  const takenMasters = new Set(others.map((current) => current.master));

  const handleSave = (): void => {
    const saved =
      bridge === null
        ? report(addDaliBridge({ address, master, label: label.trim() }), 'DALI bridge added.')
        : report(
            updateDaliBridge(bridge.id, { address, master, label: label.trim() }),
            'DALI bridge updated.',
          );
    if (saved) onClose();
  };

  return (
    <Sheet
      isOpen={isOpen}
      title={bridge === null ? 'Add DALI bridge' : 'Edit DALI bridge'}
      subtitle="The bridge is addressed 0–15 like a rail module, and owns one DALI bus."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {bridge === null ? 'Add bridge' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <AddressPicker
          label="Module address"
          hint="Greyed addresses are already used by another bridge."
          values={DALI_BRIDGE_ADDRESS_RANGE}
          taken={takenAddresses}
          selected={address}
          onSelect={setAddress}
        />

        <AddressPicker
          label="DALI bus (master)"
          hint="One bus carries one bridge, so a bus already in use cannot be chosen."
          values={MASTER_RANGE}
          taken={takenMasters}
          selected={master}
          onSelect={setMaster}
        />

        <Field
          label="Label"
          value={label}
          onChange={setLabel}
          placeholder="Bedroom DALI"
          hint="Optional. Helps you match the bridge to the drawing."
        />
      </div>
    </Sheet>
  );
};

export default BridgeSheet;
